import { useEffect, useRef, useState } from 'react';
import { randomTip } from '../../game/data/loadingTips';

/**
 * ShweTap boot loader. Spec: docs/LOADING_SCREEN.md · Mock: docs/loaddermock.png
 *
 * Two deliberate constraints, both because this component renders FIRST:
 *
 * 1. All artwork is inline SVG, not `<use href="#sprite">`. Injecting the icon
 *    sprite is itself one of the load steps this bar is reporting on, so the
 *    loader cannot depend on it. Shapes are drawn to match the `char_miner_*`
 *    and `rock_*` symbols so the visual language is identical.
 * 2. Keyframes live in a co-located <style> tag rather than the global stylesheet,
 *    so the loader animates even if app CSS has not landed yet.
 */

export type MiningLoaderProps = {
  /** 0–100, clamped. Driven by real load steps — never a synthetic timer. */
  progress?: number;
  tip?: string;
  /** Defaults to "DIGGING DEEPER…", becomes "TREASURE FOUND!" at 100. */
  status?: string;
  /** When set, replaces the tip panel with an inline error + retry. */
  error?: string;
  onRetry?: () => void;
  /** Fires once the animated fill actually reaches 100. */
  onFillComplete?: () => void;
};

const clamp = (n: number) => Math.max(0, Math.min(100, Math.round(n)));

/** Crack stage 1–4 across the quartiles; the bar filling IS the rock breaking. */
const crackStage = (pct: number) => (pct >= 75 ? 4 : pct >= 50 ? 3 : pct >= 25 ? 2 : 1);

export default function MiningLoader({
  progress = 0,
  tip,
  status,
  error,
  onRetry,
  onFillComplete,
}: MiningLoaderProps) {
  const target = clamp(progress);

  // Tip is chosen once per mount, not per render.
  const [tipText] = useState(() => tip ?? randomTip());

  const reduced =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const [display, setDisplay] = useState(0);
  const doneRef = useRef(false);

  // Ease toward the target at +1% / 20ms, so a 40-point jump reads as a smooth
  // 800ms fill. clearInterval lives outside the state updater on purpose —
  // updaters must stay pure or StrictMode's double-invoke breaks the timer.
  useEffect(() => {
    if (reduced) {
      setDisplay(target);
      return;
    }
    const timer = window.setInterval(() => {
      setDisplay((prev) => (prev < target ? prev + 1 : prev));
    }, 20);
    return () => window.clearInterval(timer);
  }, [target, reduced]);

  useEffect(() => {
    if (display >= 100 && !doneRef.current) {
      doneRef.current = true;
      onFillComplete?.();
    }
  }, [display, onFillComplete]);

  const complete = display >= 100;
  const statusText = status ?? (complete ? 'TREASURE FOUND!' : 'DIGGING DEEPER…');
  const stage = crackStage(display);

  return (
    <div className="w-full max-w-md rounded-2xl border border-[#3a2a1a] bg-[#1b120a] p-6 shadow-[0_18px_60px_rgba(0,0,0,.6)]">
      <style>{CSS}</style>

      {/* ── Logo lockup ───────────────────────────────────────────────── */}
      <header className="text-center">
        <PickaxeCrest />
        <h1 className="mt-1 text-[2.6rem] font-black leading-none tracking-[.06em] text-[#FFC24A] [text-shadow:0_3px_0_#8A4E10,0_5px_14px_rgba(255,163,71,.45)]">
          SHWETAP
        </h1>
        <p className="mt-2 text-[.7rem] font-bold tracking-[.22em] text-[#D8C4A8]">
          TAP. DIG. DISCOVER. EARN.
        </p>
        <p className="mt-1 text-[.68rem] text-[#9A8571]">by ReiwaSakura.Tech</p>
      </header>

      {/* ── Animated scene ───────────────────────────────────────────── */}
      <div className="relative mt-5 overflow-hidden rounded-xl border border-[#3a2a1a] bg-[#120c07]">
        <MiningScene stage={stage} complete={complete} reduced={!!reduced} />
      </div>

      {/* ── Status + progress ────────────────────────────────────────── */}
      <p
        aria-live="polite"
        className="mt-5 text-center text-[1.05rem] font-black tracking-[.12em] text-[#F5A02A]"
      >
        {statusText}
      </p>

      <div className="mt-3 flex items-center gap-3">
        <div
          role="progressbar"
          aria-label="Loading ShweTap"
          aria-valuenow={display}
          aria-valuemin={0}
          aria-valuemax={100}
          className="h-5 flex-1 overflow-hidden rounded-full border border-[#3a2a1a] bg-[#120c07] p-[3px]"
        >
          <div
            className="h-full rounded-full bg-gradient-to-r from-[#FFD86E] to-[#F0A62A] shadow-[0_0_12px_rgba(255,179,71,.4)] transition-[width] duration-150 ease-out"
            style={{ width: `${display}%` }}
          />
        </div>
        <span className="w-[3.4rem] text-right text-[1.05rem] font-black text-[#FFF3DC] [font-variant-numeric:tabular-nums]">
          {display}%
        </span>
      </div>

      {/* ── Tip / error panel ────────────────────────────────────────── */}
      {error ? (
        <div className="mt-4 rounded-xl border border-[#E0553F55] bg-[#2a1210] px-4 py-3">
          <p className="text-[.82rem] leading-snug text-[#F0B4A8]">{error}</p>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="mt-2 rounded-lg border border-[#8A5A2E] bg-[#3A2418] px-3 py-1.5 text-[.78rem] font-bold text-[#FFF3DC]"
            >
              Try again
            </button>
          )}
        </div>
      ) : (
        <div className="mt-4 flex items-start gap-3 rounded-xl border border-[#3a2a1a] bg-[#150e08] px-4 py-3">
          <BulbIcon />
          <p className="text-[.82rem] leading-snug text-[#FFF3DC]">
            <span className="font-black text-[#F5C542]">TIP:</span> {tipText}
          </p>
        </div>
      )}
    </div>
  );
}

/* ══ Inline artwork ══════════════════════════════════════════════════ */

function PickaxeCrest() {
  return (
    <svg viewBox="0 0 48 24" aria-hidden="true" className="mx-auto h-6 w-12">
      <g stroke="#C9A227" strokeWidth="2.2" strokeLinecap="round" fill="none">
        <path d="M10 20 L34 5" />
        <path d="M38 20 L14 5" />
      </g>
      <g fill="#8A8177">
        <path d="M30 3 q6 -1 9 4 q-6 1 -9 -4Z" />
        <path d="M18 3 q-6 -1 -9 4 q6 1 9 -4Z" />
      </g>
    </svg>
  );
}

function BulbIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="mt-[2px] h-5 w-5 shrink-0">
      <path
        d="M12 3a6 6 0 0 0-3.2 11.1V17h6.4v-2.9A6 6 0 0 0 12 3Z"
        fill="#FFD86E"
        stroke="#B8860B"
        strokeWidth="1.1"
      />
      <rect x="9.4" y="18" width="5.2" height="1.7" rx=".8" fill="#B8860B" />
      <rect x="10.2" y="20.4" width="3.6" height="1.5" rx=".7" fill="#8A5A2E" />
    </svg>
  );
}

/**
 * The scene. Simplified to 3 tones per material, integer-grid geometry —
 * the same rules the sprite art follows (docs/ART_DIRECTION.md §3).
 */
function MiningScene({
  stage,
  complete,
  reduced,
}: {
  stage: number;
  complete: boolean;
  reduced: boolean;
}) {
  const anim = reduced ? 'ml-static' : '';
  return (
    <svg viewBox="0 0 320 180" aria-hidden="true" className="block h-auto w-full">
      <defs>
        <radialGradient id="ml-glow" cx="50%" cy="60%" r="55%">
          <stop offset="0%" stopColor="#FFB347" stopOpacity=".55" />
          <stop offset="60%" stopColor="#C4611A" stopOpacity=".18" />
          <stop offset="100%" stopColor="#0B0705" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="ml-wall" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#3E2A1C" />
          <stop offset="100%" stopColor="#1A100B" />
        </linearGradient>
      </defs>

      {/* cave backdrop + stalactites */}
      <rect width="320" height="180" fill="url(#ml-wall)" />
      <g fill="#2E1D14">
        <path d="M28 0 l7 20 l7-20Z" />
        <path d="M84 0 l5 13 l5-13Z" />
        <path d="M232 0 l6 17 l6-17Z" />
        <path d="M286 0 l8 24 l8-24Z" />
      </g>
      <rect y="150" width="320" height="30" fill="#241710" />

      {/* torch glow behind the rock */}
      <circle cx="196" cy="118" r="74" fill="url(#ml-glow)" className={`ml-pulse ${anim}`} />

      {/* hanging lantern */}
      <g className={`ml-flicker ${anim}`}>
        <path d="M42 0 v28" stroke="#5A3A22" strokeWidth="2" />
        <rect x="32" y="28" width="20" height="24" rx="3" fill="#3A2418" stroke="#8A5A2E" />
        <rect x="36" y="33" width="12" height="14" rx="2" fill="#FFB347" />
        <circle cx="42" cy="40" r="4" fill="#FFE6A8" />
      </g>

      {/* crate */}
      <g fill="#4A2E1A" stroke="#2E1D14">
        <rect x="12" y="124" width="30" height="26" rx="2" />
        <path d="M12 137 h30" />
      </g>

      {/* rock + damage */}
      <g>
        <path
          d="M160 150 q-14 -30 6 -44 q16 -12 34 -6 q22 6 24 26 q2 18 -12 24Z"
          fill="#6B6259"
          stroke="#4A423B"
          strokeWidth="2"
        />
        <path d="M172 108 q14 -6 26 2" fill="none" stroke="#8A8177" strokeWidth="2" />
        <g stroke="#241F1B" strokeWidth="2" fill="none" strokeLinecap="round">
          <path d="M186 150 L184 126" />
          {stage >= 2 && <path d="M184 126 L168 116" />}
          {stage >= 3 && <path d="M184 126 L204 114" />}
          {stage >= 4 && <path d="M204 114 L214 126 M168 116 L162 130" />}
        </g>
        {stage >= 4 && (
          <g stroke="#E8B24C" strokeWidth="1.4" fill="none" opacity=".7">
            <path d="M186 148 L185 128 L200 118" />
          </g>
        )}
      </g>

      {/* impact spark */}
      <g className={`ml-spark ${anim}`}>
        <circle cx="182" cy="122" r="9" fill="#FFE6A8" opacity=".9" />
        <g stroke="#FFD86E" strokeWidth="2.4" strokeLinecap="round">
          <path d="M182 108 v-9 M182 136 v9 M168 122 h-9 M196 122 h9" />
        </g>
      </g>

      {/* miner, swinging */}
      <g className={`ml-swing ${anim}`} style={{ transformOrigin: '118px 132px' }}>
        <rect x="104" y="112" width="26" height="30" rx="4" fill="#6B4A2A" />
        <rect x="108" y="140" width="7" height="10" fill="#3A2418" />
        <rect x="120" y="140" width="7" height="10" fill="#3A2418" />
        <circle cx="117" cy="102" r="12" fill="#E8B98C" />
        <path d="M104 100 q13 -14 26 0 q-13 -5 -26 0Z" fill="#8A5A2E" />
        <circle cx="117" cy="94" r="3.4" fill="#FFE6A8" />
        <g className={`ml-axe ${anim}`} style={{ transformOrigin: '128px 116px' }}>
          <path d="M128 116 L156 96" stroke="#5A3A22" strokeWidth="4" strokeLinecap="round" />
          <path d="M150 92 q12 -3 15 7 q-11 3 -15 -7Z" fill="#8A8177" stroke="#4A423B" />
        </g>
      </g>

      {/* coins + kyat gem */}
      <g className={`ml-float ${anim}`}>
        <circle cx="238" cy="72" r="7" fill="#F5C542" stroke="#B8860B" strokeWidth="1.4" />
        <circle cx="150" cy="60" r="5.4" fill="#F5C542" stroke="#B8860B" strokeWidth="1.2" />
        <path d="M262 92 l9 -7 l9 7 l-9 12Z" fill="#A96BF0" stroke="#D9B3FF" strokeWidth="1.2" />
      </g>

      {/* dust motes */}
      <g fill="#8A8177" className={`ml-dust ${anim}`} opacity=".45">
        <circle cx="70" cy="140" r="1.6" />
        <circle cx="212" cy="146" r="1.3" />
        <circle cx="150" cy="136" r="1.1" />
        <circle cx="272" cy="142" r="1.5" />
      </g>

      {/* completion burst */}
      {complete && (
        <g className={`ml-burst ${anim}`}>
          <circle cx="186" cy="120" r="30" fill="none" stroke="#FFD86E" strokeWidth="3" />
          <circle cx="186" cy="120" r="46" fill="none" stroke="#FFB347" strokeWidth="1.6" opacity=".5" />
        </g>
      )}
    </svg>
  );
}

/* Inline so the loader animates before app CSS lands. Timings: LOADING_SCREEN.md §4. */
const CSS = `
@keyframes ml-swing{0%{transform:rotate(-7deg)}32%{transform:rotate(-9deg)}
40%{transform:rotate(6deg)}55%{transform:rotate(4deg)}100%{transform:rotate(-7deg)}}
@keyframes ml-axe{0%{transform:rotate(-45deg)}32%{transform:rotate(-52deg)}
40%{transform:rotate(25deg)}55%{transform:rotate(18deg)}100%{transform:rotate(-45deg)}}
@keyframes ml-spark{0%,30%{opacity:0;transform:scale(.4)}
40%{opacity:1;transform:scale(1.15)}58%{opacity:0;transform:scale(.7)}100%{opacity:0}}
@keyframes ml-pulse{0%,100%{transform:scale(1)}50%{transform:scale(1.04)}}
@keyframes ml-flicker{0%,100%{opacity:1}33%{opacity:.86}66%{opacity:.95}}
@keyframes ml-float{0%,100%{transform:translateY(0)}50%{transform:translateY(-5px)}}
@keyframes ml-dust{0%{transform:translateY(0);opacity:.45}100%{transform:translateY(-26px);opacity:0}}
@keyframes ml-burst{0%{opacity:1;transform:scale(.5)}100%{opacity:0;transform:scale(1.5)}}
.ml-swing{animation:ml-swing 1600ms infinite}
.ml-axe{animation:ml-axe 1600ms infinite}
.ml-spark{animation:ml-spark 1600ms infinite}
.ml-pulse{animation:ml-pulse 2200ms ease-in-out infinite}
.ml-flicker{animation:ml-flicker 220ms steps(3) infinite}
.ml-float{animation:ml-float 3400ms ease-in-out infinite}
.ml-dust{animation:ml-dust 7000ms linear infinite}
.ml-burst{animation:ml-burst 600ms ease-out forwards;transform-origin:186px 120px}
.ml-swing,.ml-axe,.ml-spark,.ml-pulse,.ml-float,.ml-burst{transform-box:fill-box}
.ml-static{animation:none!important}
@media (prefers-reduced-motion:reduce){
.ml-swing,.ml-axe,.ml-spark,.ml-pulse,.ml-flicker,.ml-float,.ml-dust,.ml-burst{animation:none!important}}
`;
